// ApiService_auth.tsx

// Request interface for login (now only needs JWT token in header)
export interface LoginRequest {
  // No body needed - everything comes from token
}

// Response interface for successful authentication
export interface AuthResponse {
  first_name: string;
  last_name: string;
  role: string;
  role_type: string;
  email: string;
  user_id: string;
}

// User data interface for session storage
export interface UserData {
  first_name: string;
  last_name: string;
  role: string;
  role_type: string;
  email: string;
  user_id: string;
}

class ApiServiceAuth {
  private authBaseUrl: string = 'http://localhost:8080/api';

  // POST for user authentication/login with JWT token
  async getRole(jwt_token: string): Promise<AuthResponse> {
    try {
      console.log('=== Starting Authentication with JWT Token ===');
      console.log('API Endpoint:', `${this.authBaseUrl}/auth/login`);
      console.log('Token received:', jwt_token ? 'Yes' : 'No');
      console.log('Token length:', jwt_token?.length);
      console.log('Token preview:', jwt_token?.substring(0, 100) + '...');
      
      const response = await fetch(`${this.authBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt_token}`
        }
        // No body needed - backend will extract everything from the token
      });
      console.log(response);
      console.log('Response status:', response.status);
      console.log('Response OK:', response.ok);
      
      // Try to get response text for debugging
      const responseText = await response.text();
      console.log('Response body:', responseText);

      if (!response.ok) {
        // Handle different error status codes
        if (response.status === 401) {
          throw new Error('Unauthorized access: Invalid or expired token');
        } else if (response.status === 404) {
          throw new Error('User not found in the system');
        } else if (response.status === 500) {
          throw new Error('Server error. Please try again later');
        } else {
          throw new Error(`Authentication failed: ${response.status}. Response: ${responseText}`);
        }
      }

      // Parse the response
      const data: AuthResponse = responseText ? JSON.parse(responseText) : {};
      
      // Log successful authentication
      console.log(' User authenticated successfully:', {
        user_id: data.user_id,
        email: data.email,
        role: data.role,
        role_type: data.role_type
      });

      return data;
    } catch (error) {
      console.error(' Authentication error:', error);
      throw error;
    }
  }

  // Helper method to store user data in sessionStorage after successful login
  storeUserSession(userData: AuthResponse): void {
    try {
      // Store user data in sessionStorage (aligned with Azure AD session storage)
      sessionStorage.setItem('user_data', JSON.stringify({
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role,
        role_type: userData.role_type,
        email: userData.email,
        user_id: userData.user_id
      }));
      
      // Store login timestamp
      sessionStorage.setItem('login_timestamp', new Date().toISOString());
      
      console.log(' User session stored successfully');
    } catch (error) {
      console.error('Error storing user session:', error);
      throw error;
    }
  }

  // Helper method to get the stored JWT token for API calls
  getStoredToken(): string | null {
    try {
      const token = sessionStorage.getItem('app_id_token');
      if (!token) {
        console.warn('No stored token found');
        return null;
      }
      return token;
    } catch (error) {
      console.error('Error retrieving stored token:', error);
      return null;
    }
  }

  // Helper method to get authorization header for API calls
  getAuthHeader(): Record<string, string> {
    const token = this.getStoredToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'accept': 'application/json'
    };
  }

  // Helper method to retrieve stored user data
  getUserSession(): UserData | null {
    try {
      const storedData = sessionStorage.getItem('user_data');
      if (storedData) {
        return JSON.parse(storedData) as UserData;
      }
      return null;
    } catch (error) {
      console.error('Error retrieving user session:', error);
      return null;
    }
  }

  // Helper method to check if user is authenticated
  isAuthenticated(): boolean {
    try {
      const userData = this.getUserSession();
      const token = this.getStoredToken();
      return userData !== null && token !== null;
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  }

  // Helper method to get current user role
  getUserRole(): string | null {
    try {
      const userData = this.getUserSession();
      return userData ? userData.role : null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  }

  // Helper method to get current user role type
  getUserRoleType(): string | null {
    try {
      const userData = this.getUserSession();
      return userData ? userData.role_type : null;
    } catch (error) {
      console.error('Error getting user role type:', error);
      return null;
    }
  }

  // Helper method to clear user session (logout)
  clearUserSession(): void {
    try {
      sessionStorage.removeItem('user_data');
      sessionStorage.removeItem('login_timestamp');
      sessionStorage.removeItem('app_id_token');
      console.log('✅ User session cleared successfully');
    } catch (error) {
      console.error('Error clearing user session:', error);
      throw error;
    }
  }

  // Helper method to handle API errors
  handleApiError(error: any): string {
    if (error instanceof Error) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    } else {
      return 'An unexpected error occurred. Please try again.';
    }
  }

  // Example method for making authenticated API calls
  async makeAuthenticatedRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    try {
      const headers = this.getAuthHeader();
      
      const response = await fetch(`${this.authBaseUrl}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...(options.headers || {})
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token might be expired, redirect to login
          this.clearUserSession();
          window.location.reload(); // This will trigger re-authentication
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`API request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }
}

export default new ApiServiceAuth();